import type { GetterTree } from "vuex";
import type {
  Client,
  Place,
  RootState,
  Project,
  Variable,
  User,
  ProjectEnergy,
} from "@/types";
import type { ParameterState } from "@/types/store";

export const getters: GetterTree<ParameterState, RootState> = {
  // PERMISSIONS
  getPermissionsData(state): any[] {
    return state.permissions.data;
  },
  getPermissionsLoading(state): boolean {
    return state.permissions.loading;
  },
  getPermissionsCount(state): number {
    return state.permissions.count;
  },
  getPermissionsError(state): string | null {
    return state.permissions.error;
  },

  // ROLES
  getRolesData(state): any[] {
    return state.roles.data;
  },
  getRolesLoading(state): boolean {
    return state.roles.loading;
  },
  getRolesCount(state): number {
    return state.roles.count;
  },
  getRolesError(state): string | null {
    return state.roles.error;
  },

  // USERS
  getUsersData(state): User[] {
    return state.users.data;
  },
  getUsersLoading(state): boolean {
    return state.users.loading;
  },
  getUsersError(state): string | null {
    return state.users.error;
  },

  // USER
  getUserData(state): User {
    return state.user.data;
  },
  getUserLoading(state): boolean {
    return state.user.loading;
  },
  getUserError(state): string | null {
    return state.user.error;
  },

  // VARIABLE RELATION
  getVariableRelation(state): Client[] {
    return state.variableRelation.data;
  },
  getVariableRelatioLoading(state): boolean {
    return state.variableRelation.loading;
  },
  getVariableRelatioError(state): string | null {
    return state.variableRelation.error;
  },

  // CLIENTS
  getClientsData(state): Client[] {
    return state.clients.data;
  },
  getClientsLoading(state): boolean {
    return state.clients.loading;
  },
  getClientsError(state): string | null {
    return state.clients.error;
  },

  // CLIENT
  getClientData(state): Client {
    return state.client.data;
  },
  getClientLoading(state): boolean {
    return state.client.loading;
  },
  getClientError(state): string | null {
    return state.client.error;
  },

  // PLACES
  getPlacesData(state): Place[] {
    return state.places.data;
  },
  getPlacesLoading(state): boolean {
    return state.places.loading;
  },
  getPlacesError(state): string | null {
    return state.places.error;
  },

  // PLACE
  getPlaceData(state): Place {
    return state.place.data;
  },
  getPlaceLoading(state): boolean {
    return state.place.loading;
  },
  getPlaceError(state): string | null {
    return state.place.error;
  },

  //PROJECTS
  getAllProjectsData(state): Project[] {
    return state.allProjects.data;
  },
  getAllProjectsLoading(state): boolean {
    return state.allProjects.loading;
  },
  getAllProjectsError(state): string | null {
    return state.allProjects.error;
  },
  getProjectsData(state): Project[] {
    return state.projects.data;
  },
  getProjects:
    (state) =>
    (projectId: string = null) => {
      return projectId
        ? state.projects.data.filter((project: any) => project.id == projectId)
        : state.projects.data;
    },
  getProjectsLoading(state): boolean {
    return state.projects.loading;
  },
  getProjectsError(state): string | null {
    return state.projects.error;
  },
  getVariables(state): Variable[] {
    return state.variables.data;
  },
  getProjectVariables(state): any[] {
    return state.projectsVariables;
  },

  getProjectData(state): Project {
    return state.project.data;
  },
  getProjectLoading(state): boolean {
    return state.project.loading;
  },
  getProjectError(state): string | null {
    return state.project.error;
  },

  getProjecEnergytData(state): ProjectEnergy[] {
    return state.projectEnergy.data;
  },
  getProjecEnergyLoading(state): boolean {
    return state.projectEnergy.loading;
  },
  getProjecEnergyError(state): string | null {
    return state.projectEnergy.error;
  },

  getVariablesLoading(state): boolean {
    return state.variables.loading;
  },
  getVariablesError(state): string | null {
    return state.variables.error;
  },
  getVariablesCount(state): number {
    return state.variables.count;
  },
  getVariableById: (state) => (variableId: string) => {
    return state.variables.data.find(
      (variable: Variable) => variable.id === variableId,
    );
  },

  // Project Energy
  getChoices(state): any[] {
    return state.choices.data;
  },

  // BEX support
  getBexSupportProject: (state) => (projectId: string) => {
    return state.bexSupport.data.find(
      (bexSupport: any) => bexSupport.project_id === projectId,
    );
  },
  getBexSupportLoading(state): boolean {
    return state.bexSupport.loading;
  },

  // BEX support Files
  getBexSupportFiles: (state) => (projectId: string) => {
    const data = state.bexSupportFiles.data.find(
      (bexSupportFile: any) => bexSupportFile.project_id === projectId,
    );
    return data && data.files ? data.files : [];
  },
  getBexSupportFilesLoading(state): boolean {
    return state.bexSupportFiles.loading;
  },

  // Project Energy
  getBexChoices(state): any[] {
    return state.bexChoices.data;
  },
  //BILAN
  getBilanByProject: (state) => (projectId: string) => {
    const bilan = state.bilans.data.find(
      (bilan: any) => bilan.project_id === projectId,
    );

    if (bilan) {
      // Sort the outer list of balanceAapes by year descending
      bilan.balanceAapes.sort((a: any, b: any) => b.year - a.year);

      // For each balanceAape, sort the inner list by number ascending
      bilan.balanceAapes.forEach((balanceAape: any) => {
        balanceAape.balanceAape.sort((a: any, b: any) => a.number - b.number);
      });

      return bilan.balanceAapes;
    }

    return [];
  },
  getBilanLoading(state): boolean {
    return state.bilans.loading;
  },
  // AAPES
  getAAPES(state): any[] {
    return state.aapes.data;
  },
  getAAPESLoading(state): boolean {
    return state.aapes.loading;
  },
  getAAPESError(state): string | null {
    return state.aapes.error;
  },
  getAAPESById: (state) => (aapeId: string) => {
    return state.aapes.data.find((aapeData: any) => aapeData.id === aapeId);
  },
  getAAPESByProject: (state) => (projectId: string) => {
    return state.aapes.dataWithDetails
      ? state.aapes.dataWithDetails.find(
          (aapeData: any) => aapeData.project.id === projectId,
        )
      : undefined;
  },
  getAAPEDetail(state): any[] {
    return state.aapes.dataWithDetails
      ? state.aapes.dataWithDetails.sort(
          (a: any, b: any) => a.number - b.number,
        )
      : state.aapes.dataWithDetails;
  },

  getAAPEByIdDetail: (state) => (aapeId: string) => {
    return (
      state.aapes.dataWithDetails &&
      state.aapes.dataWithDetails.find(
        (aapeData: any) => aapeData.id === aapeId,
      )
    );
  },

  // PERIMETER
  getPerimeters(state): any[] {
    return state.perimetres.data;
  },
  getPerimetersLoading(state): boolean {
    return state.perimetres.loading;
  },
  getPerimetersError(state): string | null {
    return state.perimetres.error;
  },
  getPerimeterById: (state) => (aapeId: string) => {
    return state.perimetres.data.find(
      (perimetreData: any) => perimetreData.id === aapeId,
    );
  },
  getPerimetrsDetailByAAPE: (state) => (aapeId: string) => {
    return state.perimetres.data
      .map((perimeterItem: any) => {
        if (perimeterItem.aape?.id === aapeId) return perimeterItem;
      })
      .filter((item: any) => item !== undefined);
  },

  // Variable
  getVariable(state): Variable {
    return state.variable.data;
  },
  getVariableLoading(state): boolean {
    return state.variable.loading;
  },
  getVariableError(state): string | null {
    return state.variable.error;
  },

  // Manuel Variable
  getManualVariable(state): Variable {
    return state.manualVariable.data;
  },
  getManualVariableLoading(state): boolean {
    return state.manualVariable.loading;
  },
  getManualVariableError(state): string | null {
    return state.manualVariable.error;
  },

  // VariableData
  getVariableData(state): any {
    return state.variableDatas.data;
  },
  getVariableDataLoading(state): boolean {
    return state.variableDatas.loading;
  },
  getVariableDataError(state): string | null {
    return state.variableDatas.error;
  },

  getVariablesByProjectIdLoading(state): boolean {
    return state.variablesByProject.loading;
  },
  getVariablesByProjectIdError(state): string | null {
    return state.variablesByProject.error;
  },
  getVariablesByProjectIdCount(state): number {
    return state.variablesByProject.count;
  },
  getVariablesByProjectIdVariables(state): any[] {
    return state.variablesByProject.data;
  },
};
